import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Award,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  History,
  LogOut,
  Settings,
  Upload,
  type LucideIcon,
} from "lucide-react";
import { logout as logoutRequest, updateMyAvatar } from "../api/auth";
import { uploadImage, fileUrl } from "../api/files";
import { myCourses, type MyCourseSummary } from "../api/assignments";
import { ApiError } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { ROLE_LABELS } from "../types/user";
import { MobileTabBar } from "../components/MobileTabBar";
import { FieldPracticeSection } from "../components/FieldPracticeSection";
import { MyCertificatesSection } from "../components/MyCertificatesSection";
import { UpcomingExamCard } from "../components/UpcomingExamCard";

export function StudentProfilePage() {
  const { user, setUser } = useCurrentUser();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [courses, setCourses] = useState<MyCourseSummary[] | null>(null);

  useEffect(() => {
    void myCourses()
      .then((r) => setCourses(r.items))
      .catch(() => setCourses([]));
  }, []);

  if (!user) return null;

  async function handleAvatarPick(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const result = await uploadImage(f);
      const updated = await updateMyAvatar(result.id);
      setUser(updated);
    } catch (err) {
      alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleLogout() {
    await logoutRequest();
    setUser(null);
    navigate("/login", { replace: true });
  }

  const totalCourses = courses?.length ?? 0;
  const totalLessons = (courses ?? []).reduce(
    (acc, c) => acc + c.lessons_total,
    0,
  );
  const completedLessons = (courses ?? []).reduce(
    (acc, c) => acc + c.lessons_completed,
    0,
  );
  const completedCourses = (courses ?? []).filter((c) => c.percent === 100)
    .length;

  const roleLabel =
    user.roles
      .map((r) => ROLE_LABELS[r] ?? r)
      .slice(0, 1)
      .join("") || "Ученик";

  return (
    <div className="min-h-full bg-surface pb-24 md:pb-8">
      <header className="sticky top-0 z-10 border-b border-line bg-surface/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center px-4 py-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="grid h-9 w-9 place-items-center rounded-lg text-ink-soft transition hover:bg-surface-alt"
            aria-label="Назад"
          >
            <ChevronLeft size={20} strokeWidth={2} />
          </button>
          <h1 className="flex-1 text-center text-base font-semibold text-ink">
            Профиль
          </h1>
          <button
            type="button"
            className="grid h-9 w-9 place-items-center rounded-lg text-ink-soft transition hover:bg-surface-alt"
            aria-label="Настройки"
            disabled
          >
            <Settings size={18} strokeWidth={2} />
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 pt-5">
        {/* Avatar card */}
        <section className="card flex items-center gap-4 p-5">
          <div className="relative shrink-0">
            {user.avatar_file_id ? (
              <img
                src={fileUrl(user.avatar_file_id)}
                alt=""
                className="h-20 w-20 rounded-full object-cover ring-2 ring-line"
              />
            ) : (
              <div className="grid h-20 w-20 place-items-center rounded-full bg-brand/10 text-2xl font-bold uppercase text-brand">
                {user.full_name.trim().charAt(0) || "?"}
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/avif"
              onChange={handleAvatarPick}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="absolute -bottom-1 -right-1 grid h-8 w-8 place-items-center rounded-full bg-brand text-white shadow-sm transition hover:bg-brand-dark disabled:opacity-60"
              aria-label="Сменить аватар"
            >
              <Upload size={14} strokeWidth={2.25} />
            </button>
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-base font-semibold text-ink">
              {user.full_name}
            </div>
            <div className="mt-0.5 text-xs text-ink-muted">{roleLabel}</div>
            <div className="mt-2 space-y-0.5 text-sm text-ink-soft">
              {user.phone && (
                <div className="truncate">
                  <span className="text-ink-muted">Тел.: </span>
                  {user.phone}
                </div>
              )}
              <div className="truncate">
                <span className="text-ink-muted">Логин: </span>
                {user.login}
              </div>
            </div>
          </div>
        </section>

        {/* Progress summary */}
        <section className="card mt-4 p-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
            Общий прогресс
          </div>
          <div className="mt-3 flex items-center justify-between">
            <ProgressStat label="Курсы" current={completedCourses} total={totalCourses} />
            <div className="h-10 w-px bg-line" aria-hidden />
            <ProgressStat
              label="Уроки"
              current={completedLessons}
              total={totalLessons}
            />
          </div>
        </section>

        <UpcomingExamCard />
        <FieldPracticeSection />
        <MyCertificatesSection />

        {/* Action list */}
        <section className="mt-4 overflow-hidden rounded-2xl bg-white ring-1 ring-line">
          <ActionRow icon={History} label="История тестов" disabled />
          <ActionRow icon={Award} label="Мои достижения" disabled />
          <ActionRow icon={ClipboardList} label="Сданные работы" disabled />
          <ActionRow icon={BookOpen} label="Мои курсы" to="/my-courses" />
          <ActionRow icon={Settings} label="Настройки" disabled isLast />
        </section>

        <button
          type="button"
          onClick={handleLogout}
          className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-line bg-white px-4 py-3 text-sm font-medium text-brand-danger transition hover:bg-brand-danger/5"
        >
          <LogOut size={16} strokeWidth={2.25} />
          Выйти
        </button>

        <p className="mt-3 text-center text-[11px] text-ink-muted/70">
          Петрополь Академия
        </p>
      </main>

      <MobileTabBar active="profile" />
    </div>
  );
}

function ProgressStat({
  label,
  current,
  total,
}: {
  label: string;
  current: number;
  total: number;
}) {
  return (
    <div className="flex-1 text-center">
      <div className="text-xs text-ink-muted">{label}</div>
      <div className="mt-1 text-lg font-bold tabular-nums text-ink">
        {current}
        <span className="text-sm font-medium text-ink-muted"> из {total}</span>
      </div>
    </div>
  );
}

function ActionRow({
  icon: Icon,
  label,
  to,
  disabled,
  isLast,
}: {
  icon: LucideIcon;
  label: string;
  to?: string;
  disabled?: boolean;
  isLast?: boolean;
}) {
  const className = `flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition ${
    isLast ? "" : "border-b border-line"
  } ${disabled ? "cursor-not-allowed text-ink-muted/60" : "text-ink hover:bg-surface-alt"}`;

  const inner = (
    <>
      <Icon size={18} strokeWidth={2} />
      <span className="flex-1 font-medium">{label}</span>
      {disabled ? (
        <span className="rounded-full bg-surface-alt px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-ink-muted">
          Скоро
        </span>
      ) : (
        <ChevronRight size={16} strokeWidth={2} className="text-ink-muted" />
      )}
    </>
  );

  if (disabled || !to) {
    return (
      <div className={className} aria-disabled="true">
        {inner}
      </div>
    );
  }
  return (
    <Link to={to} className={className}>
      {inner}
    </Link>
  );
}
